<template>
  <div id="docu-responsibles-table">
    <div id="title-container">
      <h2>{{ t('docuResponsiblesTable.docuResponsibles', 2) }}</h2>
      <div id="counter">{{ docuResponsibles?.length ?? 0 }}</div>
    </div>

    <table-wrapper
      v-model:page-options="pageOptions"
      v-model:filters="filters"
      :fields="tableFields"
      :filter-configuration="filterConfiguration"
      :items="docuResponsibles"
      :loading="isLoading"
      :mode="TableMode.CLIENT_SIDE"
      selection
      @update:selected="handleSelectionChange"
    >
    </table-wrapper>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import axios from 'axios';
  import endpointsList from '@/helpers/endpointsList';
  import { AdGroup, IDocuResponsible, IDocuResponsibleDto } from 'types';
  import {
    TableFieldsBuilder,
    TableFiltersBuilder,
    TableMode,
    useTableWrapper,
  } from '@/components/TableWrapper';
  import TableWrapper from '../TableWrapper/TableWrapper.vue';
  import { DocuResponsiblesTableMode } from './DocuResponsiblesTable.types';

  defineProps<{
    mode?: DocuResponsiblesTableMode;
    addedDocuResponsibles?: string[];
  }>();

  const { t } = useI18n();
  const { createErrorToast } = useToast();

  const { pageOptions, filters, isLoading } = useTableWrapper<IDocuResponsible>(
    TableMode.CLIENT_SIDE,
    'lastName',
    getDocuResponsibles,
    undefined,
    false,
  );

  const docuResponsibles = ref<IDocuResponsible[]>([]);
  const tableFields = computed(() =>
    new TableFieldsBuilder<IDocuResponsible>({
      sortable: true,
      labelTranslationKey: 'generic',
    })
      .addField('firstName', { width: '36%' })
      .addField('lastName', { width: '30%' })
      .addField('email', { width: '34%' })
      .build(),
  );

  const filterConfiguration = new TableFiltersBuilder<IDocuResponsible>({
    translationKey: 'generic',
  })
    .addInputFilter('firstName', { width: '208px' })
    .addInputFilter('lastName', { width: '208px' })
    .addInputFilter('email', { width: '208px' })
    .build();

  const emit = defineEmits<{
    (
      e: 'update:selected-docu-responsibles',
      selected: IDocuResponsible[],
    ): void;
  }>();

  function handleSelectionChange(
    selectedDocuResponsibles: IDocuResponsible[],
  ): void {
    emit('update:selected-docu-responsibles', selectedDocuResponsibles);
  }

  async function getDocuResponsibles() {
    try {
      const response = await axios.get<IDocuResponsibleDto[]>(
        endpointsList.adIntegration.getAllUsersFromDigitalizationProjectAdGroup(
          AdGroup.DOCU_RESPONSIBLE,
        ),
      );
      docuResponsibles.value = response.data;
    } catch {
      createErrorToast(
        t('docuResponsiblesTable.errors.couldNotGetDocuResponsibles'),
      );
    }
  }
</script>

<style lang="scss" scoped>
  #docu-responsibles-table {
    border-top: 1px solid $border-color;
    padding-top: $tab-view-margin-top;

    #title-container {
      display: flex;
      align-items: center;
      column-gap: 10px;
      margin-bottom: 20px;

      h2 {
        @include subheading;
      }

      #counter {
        align-self: flex-start;
        @include heading-counter;
      }
    }
  }
</style>
